<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import WhatsappInteractiveMessageTemplatesAPI from 'dashboard/api/whatsappInteractiveMessageTemplates';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import InteractiveEditor from 'dashboard/components/widgets/conversation/WhatsappInteractive/Modal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const inboxes = useMapGetter('inboxes/getInboxes');

const templates = ref([]);
const searchQuery = ref('');
const isLoading = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);
const activeTemplate = ref(null);
const editorRef = ref(null);
const deleteDialogRef = ref(null);

const hasWhatsappCloudInbox = computed(() =>
  inboxes.value.some(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' &&
      inbox.provider === 'whatsapp_cloud'
  )
);

const filteredTemplates = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase();
  if (!query) return templates.value;
  return templates.value.filter(template =>
    [template.name, template.content].some(value =>
      value?.toLocaleLowerCase().includes(query)
    )
  );
});

const typeLabels = computed(() => ({
  button: t('CONVERSATION.INTERACTIVE.TYPES.BUTTON'),
  cta_url: t('CONVERSATION.INTERACTIVE.TYPES.CTA_URL'),
  location_request_message: t('CONVERSATION.INTERACTIVE.TYPES.LOCATION'),
  flow: t('CONVERSATION.INTERACTIVE.TYPES.FLOW'),
  catalog_message: t('CONVERSATION.INTERACTIVE.TYPES.CATALOG'),
}));

const typeLabel = template =>
  typeLabels.value[template.interactive?.type] || typeLabels.value.button;

const fetchTemplates = async () => {
  isLoading.value = true;
  try {
    const { data } = await WhatsappInteractiveMessageTemplatesAPI.get();
    templates.value = data;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.INTERACTIVE.TEMPLATES.LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const openCreate = () => editorRef.value?.open();
const openEdit = template => editorRef.value?.open(template);

const saveTemplate = async payload => {
  isSaving.value = true;
  const { id, ...attributes } = payload;
  try {
    if (id) {
      await WhatsappInteractiveMessageTemplatesAPI.updateTemplate(
        id,
        attributes
      );
    } else {
      await WhatsappInteractiveMessageTemplatesAPI.createTemplate(attributes);
    }
    editorRef.value?.close();
    await fetchTemplates();
    useAlert(t('CONVERSATION.INTERACTIVE.TEMPLATES.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.INTERACTIVE.TEMPLATES.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const duplicateTemplate = async template => {
  const names = new Set(templates.value.map(item => item.name));
  const baseName = t('CONVERSATION.INTERACTIVE.TEMPLATES.COPY_NAME', {
    name: template.name,
  });
  let name = baseName;
  let index = 2;
  while (names.has(name)) {
    name = `${baseName} ${index}`;
    index += 1;
  }

  try {
    await WhatsappInteractiveMessageTemplatesAPI.createTemplate({
      name,
      content: template.content,
      interactive: template.interactive,
    });
    await fetchTemplates();
    useAlert(t('CONVERSATION.INTERACTIVE.TEMPLATES.DUPLICATE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.INTERACTIVE.TEMPLATES.SAVE_ERROR')
    );
  }
};

const openDelete = template => {
  activeTemplate.value = template;
  deleteDialogRef.value?.open();
};

const deleteTemplate = async () => {
  if (!activeTemplate.value) return;
  isDeleting.value = true;
  try {
    await WhatsappInteractiveMessageTemplatesAPI.delete(
      activeTemplate.value.id
    );
    deleteDialogRef.value?.close();
    activeTemplate.value = null;
    await fetchTemplates();
    useAlert(t('CONVERSATION.INTERACTIVE.TEMPLATES.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.INTERACTIVE.TEMPLATES.DELETE_ERROR')
    );
  } finally {
    isDeleting.value = false;
  }
};

onMounted(async () => {
  await store.dispatch('inboxes/getFromNetwork');
  if (!hasWhatsappCloudInbox.value) {
    useAlert(t('CONVERSATION.INTERACTIVE.TEMPLATES.CLOUD_REQUIRED'));
    router.replace({ name: 'canned_list' });
    return;
  }
  fetchTemplates();
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="t('CONVERSATION.INTERACTIVE.TEMPLATES.PAGE_TITLE')"
        :description="t('CONVERSATION.INTERACTIVE.TEMPLATES.PAGE_DESCRIPTION')"
        :search-placeholder="t('CONVERSATION.INTERACTIVE.TEMPLATES.SEARCH')"
      >
        <template #count>
          <span class="text-body-main text-n-slate-11">
            {{
              t('CONVERSATION.INTERACTIVE.TEMPLATES.COUNT', {
                count: templates.length,
              })
            }}
          </span>
        </template>
        <template #actions>
          <Button
            size="sm"
            icon="i-lucide-plus"
            :label="t('CONVERSATION.INTERACTIVE.TEMPLATES.NEW')"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div
        v-if="filteredTemplates.length"
        class="grid grid-cols-1 gap-4 lg:grid-cols-2"
      >
        <article
          v-for="template in filteredTemplates"
          :key="template.id"
          class="flex min-w-0 flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-5"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <h3 class="mb-1 truncate text-base font-semibold text-n-slate-12">
                {{ template.name }}
              </h3>
              <span
                class="inline-flex rounded-md bg-n-teal-3 px-2 py-1 text-xs font-medium text-n-teal-11"
              >
                {{ typeLabel(template) }}
              </span>
            </div>
            <div class="flex shrink-0 gap-1">
              <Button
                v-tooltip.top="
                  t('CONVERSATION.INTERACTIVE.TEMPLATES.DUPLICATE')
                "
                icon="i-lucide-copy"
                color="slate"
                variant="ghost"
                size="sm"
                @click="duplicateTemplate(template)"
              />
              <Button
                v-tooltip.top="t('CONVERSATION.INTERACTIVE.TEMPLATES.EDIT')"
                icon="i-lucide-pencil"
                color="slate"
                variant="ghost"
                size="sm"
                @click="openEdit(template)"
              />
              <Button
                v-tooltip.top="t('CONVERSATION.INTERACTIVE.TEMPLATES.DELETE')"
                icon="i-lucide-trash-2"
                color="ruby"
                variant="ghost"
                size="sm"
                @click="openDelete(template)"
              />
            </div>
          </div>

          <p
            class="mb-0 line-clamp-4 whitespace-pre-wrap text-sm text-n-slate-11"
          >
            {{ template.content }}
          </p>

          <div
            v-if="template.interactive?.buttons?.length"
            class="flex flex-wrap gap-2 border-t border-n-weak pt-3"
          >
            <span
              v-for="button in template.interactive.buttons"
              :key="button.title"
              class="rounded-md border border-n-weak bg-n-solid-2 px-2 py-1 text-xs text-n-slate-12"
            >
              {{ button.title }}
            </span>
          </div>
        </article>
      </div>

      <div
        v-else
        class="flex min-h-64 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-n-strong bg-n-solid-1 p-8 text-center"
      >
        <span
          class="grid size-12 place-items-center rounded-xl bg-n-teal-3 text-n-teal-11"
        >
          <span class="i-lucide-message-square-more size-6" />
        </span>
        <h3 class="mb-0 text-base font-semibold text-n-slate-12">
          {{ t('CONVERSATION.INTERACTIVE.TEMPLATES.EMPTY_TITLE') }}
        </h3>
        <p class="mb-0 max-w-md text-sm text-n-slate-11">
          {{ t('CONVERSATION.INTERACTIVE.TEMPLATES.EMPTY_DESCRIPTION') }}
        </p>
        <Button
          icon="i-lucide-plus"
          :label="t('CONVERSATION.INTERACTIVE.TEMPLATES.NEW')"
          @click="openCreate"
        />
      </div>
    </template>
  </SettingsLayout>

  <InteractiveEditor
    ref="editorRef"
    purpose="template"
    :is-loading="isSaving"
    @save="saveTemplate"
  />

  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('CONVERSATION.INTERACTIVE.TEMPLATES.DELETE_TITLE')"
    :description="
      t('CONVERSATION.INTERACTIVE.TEMPLATES.DELETE_DESCRIPTION', {
        name: activeTemplate?.name,
      })
    "
    :confirm-button-label="t('CONVERSATION.INTERACTIVE.TEMPLATES.DELETE')"
    :is-loading="isDeleting"
    @confirm="deleteTemplate"
  />
</template>
